import EventEmitter from "events";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { TValidator } from "./Validators";
import { useForm } from "./Form";
import GUI from "../GUI";
import Debounce from "../../libs/observer/Debounce";

export type TFormControlContext = {
    valid:boolean
    dirty:boolean
    ready:boolean
    validate:(value:any,markAsDirty:boolean) => string|null
    setValue:(value:any) => void
    setDirty:(dirty:boolean) => void
}

const FormControlContext = createContext<TFormControlContext>({} as TFormControlContext)

export const useFormControl = () => useContext(FormControlContext)

export interface IFormControl extends EventEmitter {
    id:string
    valid:boolean
    dirty:boolean
    setDirty:(dirty:boolean) => IFormControl
    setValid:(valid:boolean) => IFormControl
}
class _FormControl extends EventEmitter implements IFormControl {
    id:string
    valid:boolean
    dirty:boolean
    constructor(id:string,valid:boolean,dirty:boolean) {
        super()
        this.id = id
        this.valid = valid
        this.dirty = dirty
    }
    setDirty(dirty:boolean){
        if (dirty!=this.dirty) {
            this.dirty = dirty;
            this.notify()
        }
        return this
    }
    setValid(valid:boolean){ 
        if (valid!=this.valid) {
            this.valid = valid
            this.notify()
        }
        return this 
    }
    notify(){this.emit("update", this.id, this); return this}
}

var g_id = 0
export type TFormControlProps = React.ComponentPropsWithoutRef<"div"> & {
    updateDelay?:number
    dirty?:boolean
    defaultDirty?:boolean
    children:JSX.Element
    validators:TValidator[]
}
export default function FormControl(props:TFormControlProps) {
    const {className,dirty:_dirty,defaultDirty,children,validators,updateDelay,...controlProps} = props
    const [valid,setValid] = useState<boolean>(false)
    const [dirty,setDirty] = useState<boolean>(_dirty||defaultDirty||false)
    const [error,setError] = useState<string>("")
    const [ready,setReady] = useState<boolean>(false)

    const initialized = useRef<boolean>(false)
    const controlId = useRef<string>(`fc_${g_id++}`)
    const {register,unregister} = useForm()

    const update$ = useMemo(() => new Debounce(updateDelay ?? 250), [])

    const validate = (value:any, markAsDirty=true) => {
        if (markAsDirty) {
            formControl.setDirty(true)
            setDirty(true)
        }
        for(const validator of props.validators) {
            const error = validator(value)
            if (error) {
                setError(error)
                setValid(false)
                formControl.setValid(false)
                return error
            }
        }
        setError("")
        setValid(true)
        formControl.setValid(true)
        return null
    }

    const formControl = useMemo<_FormControl>(() => new _FormControl(controlId.current, props.dirty||props.defaultDirty||false, false), [])
    const _setValue = (val:any) => {
        if (initialized.current) update$.next(val)
        else {
            initialized.current = true
            validate(val, false)
        }
    }
    const _setDirty = (_dirty:boolean) => {
        setDirty(_dirty)
        formControl.setDirty(_dirty)
    }

    useEffect(() => {
        register(controlId.current, formControl)
        update$.subscribe(validate)
        setReady(true)
        return () => { unregister(controlId.current); update$.unsubscribe(validate) }
    }, [])

    const errorMsg = error && dirty ? <div className="error">{error}</div> : null
    return <FormControlContext.Provider value={{ready,valid,dirty,validate,setValue:_setValue,setDirty:_setDirty}}>
        <div className={GUI.joinClass("formControl",className,valid?"valid":"invalid",dirty?"dirty":"pristine")} {...controlProps}>
            {children}
            {errorMsg}
        </div>
    </FormControlContext.Provider>
}